import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../store/auth/selectors";
import {
  Button, Typography, message, Card, Table, Tag, Space,
  Divider, Input, Select, Row, Col, Modal, Progress, Spin, Tooltip,
} from "antd";
import {
  SyncOutlined, SearchOutlined, ClearOutlined, CheckCircleFilled,
  FileExcelOutlined, UserOutlined, FilterOutlined,
  LeftOutlined, RightOutlined, DownloadOutlined, BarChartOutlined,
} from "@ant-design/icons";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import logo from "../../assets/Logo_Prosperidad_Social.png";
import { actualizarDatosTemporalRit } from "../../services/reporteRit/temporalRitService";
import {
  obtenerEncuestadosPaginado,
  obtenerEncuestadosPorMunicipio,
  obtenerEncuestadosPorDepartamento,
  obtenerMunicipios,
  obtenerDepartamentos,
  obtenerTotalEncuestados,
  obtenerTodosEncuestadosEnBloques,
  obtenerEncuestadosPorDocumento,
  obtenerEncuestadosPorDeseaParticipar,
  obtenerEncuestadoresDisponibles,
  obtenerEncuestadosPorPromotor,
} from "../../services/reporteRit/promotorEncuestadoService";
import { DashboardEncuestas } from "../Participante/Participantes/DahsboardPromotorEncuestas/DashboardEncuestas";

const { Title, Text } = Typography;
const { Option } = Select;

const PAGE_SIZE_OPTIONS = [300, 500, 1000];
const DEFAULT_PAGE_SIZE = 300;
const DEBOUNCE_MS       = 500;

const MENSAJES_PROGRESO = [
  "Conectando con el servidor...",
  "Obteniendo registros del API RIT...",
  "Procesando información recibida...",
  "Limpiando datos anteriores...",
  "Insertando registros en lotes...",
  "Validando integridad de los datos...",
  "Guardando cambios en la base de datos...",
  "Finalizando sincronización...",
];

const nombreCompleto = (row) =>
  [row.titular_nombre1, row.titular_nombre2, row.titular_apellido1, row.titular_apellido2]
    .filter(Boolean).join(" ");

const nombrePromotor = (row) =>
  [row.promotor_nombres, row.promotor_apellidos].filter(Boolean).join(" ");

const ReporteRit = () => {
  const [loadingConsultar, setLoadingConsultar] = useState(false);

  const [rawData,         setRawData]         = useState([]);
  const [progresoResumen, setProgresoResumen]  = useState(0);
  const [loadingResumen,  setLoadingResumen]   = useState(false);
  const [filasResumen,    setFilasResumen]     = useState(0);

  // ── Paginación por cursor ─────────────────────────────────────────────────
  const [cursors,     setCursors]     = useState([0]);
  const [cursorIndex, setCursorIndex] = useState(0);
  const [hasMore,     setHasMore]     = useState(false);
  const [nextCursor,  setNextCursor]  = useState(null);
  const [pageSize,    setPageSize]    = useState(DEFAULT_PAGE_SIZE);

  const [modoFiltro,  setModoFiltro]  = useState("paginado");

  // "encuestador" | "municipio" | false
  const [modoResumen, setModoResumen] = useState(false);

  const [rawDataResumen, setRawDataResumen] = useState([]);

  // ── Estado admin ──────────────────────────────────────────────────────────
  const [municipios,           setMunicipios]           = useState([]);
  const [municipioAdmin,       setMunicipioAdmin]       = useState(null);
  const [departamentos,        setDepartamentos]        = useState([]);
  const [departamentoAdmin,    setDepartamentoAdmin]    = useState(null);
  const [loadingMunicipios,    setLoadingMunicipios]    = useState(false);
  const [loadingDepartamentos, setLoadingDepartamentos] = useState(false);
  const [totalRegistros,       setTotalRegistros]       = useState(0);

  // ── Descarga Excel ────────────────────────────────────────────────────────
  const [descargando,      setDescargando]      = useState(false);
  const [progresoDescarga, setProgresoDescarga] = useState(0);
  const [filasDescargadas, setFilasDescargadas] = useState(0);

  // ── Filtros de tabla ──────────────────────────────────────────────────────
  const [filtroDocumento,       setFiltroDocumento]       = useState("");
  const [filtroPromotor,        setFiltroPromotor]        = useState(null);
  const [filtroDeseaParticipar, setFiltroDeseaParticipar] = useState(null);

  // ── Catálogo de encuestadores ─────────────────────────────────────────────
  const [encuestadores,        setEncuestadores]        = useState([]);
  const [loadingEncuestadores, setLoadingEncuestadores] = useState(false);

  // ── Toggle dashboard ──────────────────────────────────────────────────────
  const [mostrarDashboard, setMostrarDashboard] = useState(false);

  // ── Modal sincronización ──────────────────────────────────────────────────
  const [modalVisible,             setModalVisible]             = useState(false);
  const [progreso,                 setProgreso]                 = useState(0);
  const [mensajeActual,            setMensajeActual]            = useState(MENSAJES_PROGRESO[0]);
  const [sincronizacionFinalizada, setSincronizacionFinalizada] = useState(false);
  const [totalSincronizado,        setTotalSincronizado]        = useState(0);

  const intervalRef    = useRef(null);
  const mensajeIdxRef  = useRef(0);
  const debounceDocRef = useRef(null);

  const usernameFromState = useSelector((state) => state.auth.user);
  const appName           = "reporteRIT";
  const permissions       = useSelector((state) => selectAppPermissions(state, appName));
  const permissionUpdate  = permissions.priv_update === "Y";
  const esAdmin           = usernameFromState?.role === "1";
  const esCoordinador     = usernameFromState?.role === "2";

  // ── Carga inicial ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (esAdmin) {
      cargarMunicipios();
      cargarDepartamentos();
      cargarPaginado(0, DEFAULT_PAGE_SIZE, true);
      obtenerTotalEncuestados().then(setTotalRegistros).catch(console.error);
    } else {
      cargarTablaMunicipio();
    }
    cargarEncuestadores();
  }, []);

  // ── Mapeo de filas ────────────────────────────────────────────────────────
  const mapearFilas = (items) =>
    items.map((item) => ({
      key:              item.id_temporal_rit ?? item.id_encuesta ?? `${item.promotor_id}-${item.documento_titular}`,
      promotorId:       item.promotor_id,
      promotor:         nombrePromotor(item),
      municipioId:      item.municipio_id,
      municipio:        item.municipio    || "—",
      departamentoId:   item.departamento_id,
      departamento:     item.departamento || "—",
      documentoTitular: item.documento_titular     || "—",
      telefono:         item.telefono || "—",
      titular:          nombreCompleto(item)        || "—",
      direccion:        item.direccion_participante || "—",
      idEncuesta:       item.id_encuesta,
      fechaCarga:       item.fecha_carga
        ? new Date(item.fecha_carga).toLocaleString("es-CO") : "—",
      desea_participar: item.desea_participar != null ? Number(item.desea_participar) : null,
      _raw: item,
    }));

  const mapearFilasExport = (items) =>
    items.map((item) => ({
      "ID Encuesta":       item.id_encuesta        ?? "—",
      "Documento Titular": item.documento_titular  || "—",
      "Teléfono":          item.telefono           || "—",
      "Titular":           nombreCompleto(item)    || "—",
      "Dirección":         item.direccion_participante || "—",
      "Municipio":         item.municipio          || "—",
      "Departamento":      item.departamento       || "—",
      "Encuestador":       nombrePromotor(item),
      "Promotor ID":       item.promotor_id,
      "Fecha Carga":       item.fecha_carga
        ? new Date(item.fecha_carga).toLocaleString("es-CO") : "—",
      "Desea Participar":  item.desea_participar != null
        ? (Number(item.desea_participar) === 1 ? "Continua" : "No Continua") : "—",
    }));

  // ── Paginación ────────────────────────────────────────────────────────────
  const cargarPaginado = useCallback(async (
    lastId     = 0,
    size       = DEFAULT_PAGE_SIZE,
    reiniciar  = false,
    silencioso = false,
  ) => {
    if (!silencioso) setLoadingConsultar(true);
    try {
      const resultado = await obtenerEncuestadosPaginado(lastId, size);
      setRawData(mapearFilas(resultado.data));
      setHasMore(resultado.hasMore);
      setNextCursor(resultado.nextCursor);
      setModoFiltro("paginado");
      limpiarFiltrosSinRecargar();
      if (!silencioso)
        resultado.data.length > 0
          ? message.success(`${resultado.data.length} registros cargados`)
          : message.warning("No se encontraron registros");
      if (reiniciar) { setCursors([0]); setCursorIndex(0); }
    } catch (error) {
      console.error(error);
      if (!silencioso) message.error("Error al consultar los datos del RIT");
    } finally {
      if (!silencioso) setLoadingConsultar(false);
    }
  }, []);

  const irSiguientePagina = () => {
    if (!hasMore || nextCursor === null) return;
    const nuevosCursors = [...cursors.slice(0, cursorIndex + 1), nextCursor];
    setCursors(nuevosCursors);
    const nuevoIndex = cursorIndex + 1;
    setCursorIndex(nuevoIndex);
    cargarPaginado(nextCursor, pageSize);
  };

  const irPaginaAnterior = () => {
    if (cursorIndex === 0) return;
    const nuevoIndex = cursorIndex - 1;
    setCursorIndex(nuevoIndex);
    cargarPaginado(cursors[nuevoIndex], pageSize);
  };

  const handlePageSizeChange = (nuevoSize) => {
    setPageSize(nuevoSize);
    cargarPaginado(0, nuevoSize, true);
  };

  // ── Resumen completo ──────────────────────────────────────────────────────
  const cargarDataResumenCompleta = useCallback(async () => {
    setLoadingResumen(true);
    setProgresoResumen(0);
    setFilasResumen(0);
    try {
      let dataCompleta = [];

      if (esAdmin && modoFiltro === "paginado") {
        const todos = await obtenerTodosEncuestadosEnBloques(
          (pct, cargadas) => {
            setProgresoResumen(pct ?? 0);
            setFilasResumen(cargadas);
          },
          totalRegistros,
          500,
        );
        dataCompleta = mapearFilas(todos);
      } else if (modoFiltro === "municipio") {
        const resultado = await obtenerEncuestadosPorMunicipio(
          municipioAdmin || usernameFromState.cod_municipio
        );
        if (resultado?.success) dataCompleta = mapearFilas(resultado.data);
      } else if (modoFiltro === "departamento") {
        const resultado = await obtenerEncuestadosPorDepartamento(departamentoAdmin);
        if (resultado?.success) dataCompleta = mapearFilas(resultado.data);
      } else if (modoFiltro === "documento" && filtroDocumento) {
        const resultado = await obtenerEncuestadosPorDocumento(filtroDocumento.trim());
        if (resultado?.success) dataCompleta = mapearFilas(resultado.data);
      } else if (modoFiltro === "promotor" && filtroPromotor) {
        const resultado = await obtenerEncuestadosPorPromotor(filtroPromotor);
        if (resultado?.success) dataCompleta = mapearFilas(resultado.data);
      } else if (modoFiltro === "deseaParticipar" && filtroDeseaParticipar != null) {
        const resultado = await obtenerEncuestadosPorDeseaParticipar(filtroDeseaParticipar);
        if (resultado?.success) dataCompleta = mapearFilas(resultado.data);
      }

      setRawDataResumen(dataCompleta);
    } catch (error) {
      console.error(error);
      message.error("Error cargando resumen completo");
    } finally {
      setLoadingResumen(false);
      setProgresoResumen(0);
      setFilasResumen(0);
    }
  }, [
    esAdmin, modoFiltro, totalRegistros, municipioAdmin, departamentoAdmin,
    filtroDocumento, filtroPromotor, filtroDeseaParticipar, usernameFromState,
  ]);

  useEffect(() => {
    if (modoResumen) {
      cargarDataResumenCompleta();
    }
  }, [modoResumen, cargarDataResumenCompleta]);

  // ── Carga municipio / departamento ────────────────────────────────────────
  const cargarTablaMunicipio = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoadingConsultar(true);
    try {
      const resultado = await obtenerEncuestadosPorMunicipio(usernameFromState.cod_municipio);
      if (!resultado?.success) return;
      setRawData(mapearFilas(resultado.data));
      setModoFiltro("municipio");
      setHasMore(false);
      limpiarFiltrosSinRecargar();
      if (!silencioso)
        resultado.data.length > 0
          ? message.success(`${resultado.data.length.toLocaleString("es-CO")} registros cargados`)
          : message.warning("No se encontraron registros");
    } catch (error) {
      console.error(error);
      if (!silencioso) message.error("Error al consultar los datos del RIT");
    } finally {
      if (!silencioso) setLoadingConsultar(false);
    }
  }, [usernameFromState]);

  const cargarTablaAdminMunicipio = useCallback(async (municipioId, silencioso = false) => {
    if (!silencioso) setLoadingConsultar(true);
    try {
      if (municipioId) {
        const resultado = await obtenerEncuestadosPorMunicipio(municipioId);
        if (!resultado?.success) return;
        setRawData(mapearFilas(resultado.data));
        setModoFiltro("municipio");
        setHasMore(false);
        limpiarFiltrosSinRecargar();
        if (!silencioso)
          message.success(`${resultado.data.length.toLocaleString("es-CO")} registros del municipio`);
      } else {
        await cargarPaginado(0, pageSize, true, silencioso);
      }
    } catch (error) {
      console.error(error);
      if (!silencioso) message.error("Error al consultar los datos del RIT");
    } finally {
      if (!silencioso) setLoadingConsultar(false);
    }
  }, [pageSize, cargarPaginado]);

  const cargarTablaAdminDepartamento = useCallback(async (departamentoId, silencioso = false) => {
    if (!silencioso) setLoadingConsultar(true);
    try {
      if (departamentoId) {
        const resultado = await obtenerEncuestadosPorDepartamento(departamentoId);
        if (!resultado?.success) return;
        setRawData(mapearFilas(resultado.data));
        setModoFiltro("departamento");
        setHasMore(false);
        limpiarFiltrosSinRecargar();
        if (!silencioso)
          message.success(`${resultado.data.length.toLocaleString("es-CO")} registros del departamento`);
      } else {
        await cargarPaginado(0, pageSize, true, silencioso);
      }
    } catch (error) {
      console.error(error);
      if (!silencioso) message.error("Error al consultar los datos del RIT");
    } finally {
      if (!silencioso) setLoadingConsultar(false);
    }
  }, [pageSize, cargarPaginado]);

  // ── Catálogos ─────────────────────────────────────────────────────────────
  const cargarMunicipios = useCallback(async () => {
    setLoadingMunicipios(true);
    try {
      const resultado = await obtenerMunicipios();
      if (resultado?.success) setMunicipios(resultado.data);
    } catch { message.error("Error al cargar la lista de municipios"); }
    finally { setLoadingMunicipios(false); }
  }, []);

  const cargarDepartamentos = useCallback(async () => {
    setLoadingDepartamentos(true);
    try {
      const resultado = await obtenerDepartamentos();
      if (resultado?.success) setDepartamentos(resultado.data);
    } catch { message.error("Error al cargar la lista de departamentos"); }
    finally { setLoadingDepartamentos(false); }
  }, []);

  const cargarEncuestadores = useCallback(async () => {
    setLoadingEncuestadores(true);
    try {
      const resultado = await obtenerEncuestadoresDisponibles(
        esCoordinador ? Number(usernameFromState.cod_municipio) : null
      );
      if (resultado?.success) {
        setEncuestadores(resultado.data.map((e) => ({
          value: e.promotor_id,
          label: `${e.promotor_nombres} ${e.promotor_apellidos}`.trim(),
        })));
      }
    } catch {
      message.error("Error al cargar la lista de encuestadores");
    } finally {
      setLoadingEncuestadores(false);
    }
  }, [esCoordinador, usernameFromState]);

  // ── Handlers selectores admin ─────────────────────────────────────────────
  const handleMunicipioAdminChange = (value) => {
    setMunicipioAdmin(value ?? null);
    setDepartamentoAdmin(null);
    cargarTablaAdminMunicipio(value ?? null);
  };

  const handleDepartamentoAdminChange = (value) => {
    setDepartamentoAdmin(value ?? null);
    setMunicipioAdmin(null);
    cargarTablaAdminDepartamento(value ?? null);
  };

  // ── Handlers filtros secundarios ──────────────────────────────────────────
  const handleDocumentoChange = (e) => {
    const valor = e.target.value.replace(/\D/g, "");
    setFiltroDocumento(valor);
    clearTimeout(debounceDocRef.current);
    debounceDocRef.current = setTimeout(async () => {
      const valorTrim = valor.trim();
      if (modoFiltro === "municipio" || modoFiltro === "departamento") return;
      if (!valorTrim) {
        if (modoFiltro === "documento") await cargarPaginado(0, pageSize, true);
        return;
      }
      setLoadingConsultar(true);
      try {
        const resultado = await obtenerEncuestadosPorDocumento(valorTrim);
        if (!resultado?.success) return;
        setRawData(mapearFilas(resultado.data));
        setModoFiltro("documento");
        setHasMore(false);
        resultado.data.length > 0
          ? message.success(`${resultado.data.length} registro(s) encontrado(s)`)
          : message.warning("No se encontraron registros para ese documento");
      } catch (error) {
        if (error?.response?.status === 404) {
          setRawData([]);
          setModoFiltro("documento");
          setHasMore(false);
          message.warning("No se encontraron registros para ese documento");
        } else {
          console.error(error);
          message.error("Error al buscar por número de documento");
        }
      } finally {
        setLoadingConsultar(false);
      }
    }, DEBOUNCE_MS);
  };

  const handlePromotorChange = useCallback(async (value) => {
    setFiltroPromotor(value ?? null);
    if (!value) {
      if (modoFiltro === "promotor") await cargarPaginado(0, pageSize, true);
      return;
    }
    if (modoFiltro === "municipio" || modoFiltro === "departamento") return;
    setLoadingConsultar(true);
    try {
      const resultado = await obtenerEncuestadosPorPromotor(value);
      if (!resultado?.success) return;
      setRawData(mapearFilas(resultado.data));
      setModoFiltro("promotor");
      setHasMore(false);
      resultado.data.length > 0
        ? message.success(`${resultado.data.length.toLocaleString("es-CO")} registros del encuestador`)
        : message.warning("No se encontraron registros para ese encuestador");
    } catch (error) {
      if (error?.response?.status === 404) {
        setRawData([]);
        setModoFiltro("promotor");
        setHasMore(false);
        message.warning("No se encontraron registros para ese encuestador");
      } else {
        console.error(error);
        message.error("Error al filtrar por encuestador");
      }
    } finally {
      setLoadingConsultar(false);
    }
  }, [modoFiltro, pageSize, cargarPaginado]);

  const handleDeseaParticiparChange = useCallback(async (value) => {
    setFiltroDeseaParticipar(value ?? null);
    if (value == null) {
      if (modoFiltro === "deseaParticipar") await cargarPaginado(0, pageSize, true);
      return;
    }
    if (modoFiltro === "municipio" || modoFiltro === "departamento") return;
    setLoadingConsultar(true);
    try {
      const resultado = await obtenerEncuestadosPorDeseaParticipar(value);
      if (!resultado?.success) return;
      setRawData(mapearFilas(resultado.data));
      setModoFiltro("deseaParticipar");
      setHasMore(false);
      resultado.data.length > 0
        ? message.success(`${resultado.data.length.toLocaleString("es-CO")} registros encontrados`)
        : message.warning("No se encontraron registros con ese criterio");
    } catch (error) {
      if (error?.response?.status === 404) {
        setRawData([]);
        setModoFiltro("deseaParticipar");
        setHasMore(false);
        message.warning("No se encontraron registros con ese criterio");
      } else {
        console.error(error);
        message.error("Error al filtrar por desea participar");
      }
    } finally {
      setLoadingConsultar(false);
    }
  }, [modoFiltro, pageSize, cargarPaginado]);

  // ── dataFiltrada ──────────────────────────────────────────────────────────
  const dataFiltrada = useMemo(() => {
    return rawData.filter((row) => {
      const coincideDocumento =
        !filtroDocumento ||
        row.documentoTitular.toLowerCase().includes(filtroDocumento.toLowerCase());
      const coincidePromotor =
        !filtroPromotor || row.promotorId === filtroPromotor;
      const coincideDesea =
        filtroDeseaParticipar == null ||
        Number(row.desea_participar) === filtroDeseaParticipar;
      return coincideDocumento && coincidePromotor && coincideDesea;
    });
  }, [rawData, filtroDocumento, filtroPromotor, filtroDeseaParticipar]);

  const hayFiltrosActivos =
    !!filtroDocumento || !!filtroPromotor || filtroDeseaParticipar !== null;

  // ── resumenEncuestador: agrupado por promotor + municipio ─────────────────
  const resumenEncuestadorData = useMemo(() => {
    const dataBase = rawDataResumen.length > 0 ? rawDataResumen : dataFiltrada;

    const filtrada = dataBase.filter((row) => {
      const coincideDocumento =
        !filtroDocumento ||
        row.documentoTitular.toLowerCase().includes(filtroDocumento.toLowerCase());
      const coincidePromotor =
        !filtroPromotor || row.promotorId === filtroPromotor;
      const coincideDesea =
        filtroDeseaParticipar == null ||
        Number(row.desea_participar) === filtroDeseaParticipar;
      return coincideDocumento && coincidePromotor && coincideDesea;
    });

    const mapa = {};
    filtrada.forEach((item) => {
      const key = `${item.promotorId}-${item.municipio}`;
      if (!mapa[key]) {
        mapa[key] = {
          key,
          municipio:      item.municipio,
          encuestador:    item.promotor,
          totalEncuestas: 0,
          detalle:        [],
        };
      }
      mapa[key].totalEncuestas += 1;
      mapa[key].detalle.push(item);
    });

    return Object.values(mapa);
  }, [rawDataResumen, dataFiltrada, filtroDocumento, filtroPromotor, filtroDeseaParticipar]);

  // ── resumenMunicipio: agrupado por municipio ──────────────────────────────
  const resumenMunicipioData = useMemo(() => {
    const dataBase = rawDataResumen.length > 0 ? rawDataResumen : dataFiltrada;

    const filtrada = dataBase.filter((row) => {
      const coincideDocumento =
        !filtroDocumento ||
        row.documentoTitular.toLowerCase().includes(filtroDocumento.toLowerCase());
      const coincidePromotor =
        !filtroPromotor || row.promotorId === filtroPromotor;
      const coincideDesea =
        filtroDeseaParticipar == null ||
        Number(row.desea_participar) === filtroDeseaParticipar;
      return coincideDocumento && coincidePromotor && coincideDesea;
    });

    const mapa = {};
    filtrada.forEach((item) => {
      const key = item.municipio;
      if (!mapa[key]) {
        mapa[key] = {
          key,
          departamento:    item.departamento,
          municipio:       item.municipio,
          totalEncuestas:  0,
          totalContinua:   0,
          totalNoContinua: 0,
          detalle:         [],
        };
      }
      mapa[key].totalEncuestas += 1;
      if (Number(item.desea_participar) === 1) mapa[key].totalContinua   += 1;
      else                                      mapa[key].totalNoContinua += 1;
      mapa[key].detalle.push(item);
    });

    return Object.values(mapa).sort((a, b) =>
      String(a.municipio).localeCompare(String(b.municipio))
    );
  }, [rawDataResumen, dataFiltrada, filtroDocumento, filtroPromotor, filtroDeseaParticipar]);

  // ── Limpiar filtros ───────────────────────────────────────────────────────
  const limpiarFiltrosSinRecargar = () => {
    setFiltroDocumento("");
    setFiltroPromotor(null);
    setFiltroDeseaParticipar(null);
    clearTimeout(debounceDocRef.current);
  };

  const limpiarFiltros = () => {
    limpiarFiltrosSinRecargar();
    if (
      modoFiltro === "paginado"        ||
      modoFiltro === "promotor"        ||
      modoFiltro === "documento"       ||
      modoFiltro === "deseaParticipar"
    ) {
      cargarPaginado(0, pageSize, true);
    }
  };

  // ── Toggle resumen ────────────────────────────────────────────────────────
  const toggleResumen = (tipo) => {
    // Si ya está activo ese modo, lo cierra; si no, lo activa
    const siguiente = modoResumen === tipo ? false : tipo;
    setModoResumen(siguiente);
    if (!siguiente) setRawDataResumen([]);
  };

  // ── Excel — helpers ExcelJS ───────────────────────────────────────────────
  const aplicarEncabezadoSheet = async (workbook, worksheet, titulo, colFin) => {
    try {
      const response = await fetch(logo);
      const buffer   = await response.arrayBuffer();
      const imageId  = workbook.addImage({ buffer, extension: "png" });
      worksheet.mergeCells("A1:B3");
      worksheet.addImage(imageId, { tl: { col: 0.2, row: 0.5 }, ext: { width: 180, height: 60 } });
    } catch {
      console.log("Logo no cargado");
    }

    worksheet.mergeCells(`C1:${colFin}2`);
    const titleCell     = worksheet.getCell("C1");
    titleCell.value     = titulo;
    titleCell.font      = { size: 16, bold: true, color: { argb: "FF1B5E20" } };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };

    worksheet.mergeCells(`C3:${colFin}3`);
    const dateCell     = worksheet.getCell("C3");
    dateCell.value     = `Fecha de exportación: ${new Date().toLocaleDateString()}`;
    dateCell.alignment = { horizontal: "center" };

    worksheet.addRow([]);
  };

  const agregarTablaSheet = (worksheet, filas, headerColor = "FF217346") => {
    if (!filas.length) return;
    const headers   = Object.keys(filas[0]);
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font      = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: headerColor } };
      cell.alignment = { horizontal: "center" };
      cell.border    = {
        top: { style: "thin" }, bottom: { style: "thin" },
        left: { style: "thin" }, right: { style: "thin" },
      };
    });
    filas.forEach((fila) => {
      const row = worksheet.addRow(Object.values(fila));
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" }, bottom: { style: "thin" },
          left: { style: "thin" }, right: { style: "thin" },
        };
      });
    });
    worksheet.columns = headers.map((header) => ({
      width: Math.max(header.length, ...filas.map((row) => String(row[header] ?? "").length)) + 3,
    }));
  };

  // ── Resumen por encuestador para Excel ────────────────────────────────────
  const calcularResumenEncuestadorParaExport = (filasDetalle) => {
    const mapa = {};
    filasDetalle.forEach((row) => {
      const key = `${row["Encuestador"]}-${row["Municipio"]}`;
      if (!mapa[key]) {
        mapa[key] = {
          "Departamento":      row["Departamento"] ?? "—",
          "Municipio":         row["Municipio"]    ?? "—",
          "Encuestador":       row["Encuestador"]  ?? "—",
          "Total Encuestas":   0,
          "Total Continua":    0,
          "Total No Continua": 0,
        };
      }
      mapa[key]["Total Encuestas"] += 1;
      if (row["Desea Participar"] === "Continua")    mapa[key]["Total Continua"]    += 1;
      if (row["Desea Participar"] === "No Continua") mapa[key]["Total No Continua"] += 1;
    });
    return Object.values(mapa).sort((a, b) =>
      String(a["Municipio"]).localeCompare(String(b["Municipio"]))
    );
  };

  // ── Resumen por municipio para Excel ──────────────────────────────────────
  const calcularResumenMunicipioParaExport = (filasDetalle) => {
    const mapa = {};
    filasDetalle.forEach((row) => {
      const key = row["Municipio"];
      if (!mapa[key]) {
        mapa[key] = {
          "Departamento":      row["Departamento"] ?? "—",
          "Municipio":         row["Municipio"]    ?? "—",
          "Total Encuestas":   0,
          "Total Continua":    0,
          "Total No Continua": 0,
        };
      }
      mapa[key]["Total Encuestas"] += 1;
      if (row["Desea Participar"] === "Continua")    mapa[key]["Total Continua"]    += 1;
      if (row["Desea Participar"] === "No Continua") mapa[key]["Total No Continua"] += 1;
    });
    return Object.values(mapa).sort((a, b) =>
      String(a["Municipio"]).localeCompare(String(b["Municipio"]))
    );
  };

  // ── Genera Excel con 3 pestañas: Detalle + Resumen Encuestador + Resumen Municipio ──
  const generarExcel = async (filasDetalle) => {
    if (!filasDetalle.length) { message.warning("No hay datos para exportar"); return; }

    const workbook = new ExcelJS.Workbook();

    // ── Hoja 1: Detalle ───────────────────────────────────────────────────
    const wsDetalle = workbook.addWorksheet("Reporte RIT");
    await aplicarEncabezadoSheet(workbook, wsDetalle, "REPORTE RIT - ENCUESTADOS", "K");
    agregarTablaSheet(wsDetalle, filasDetalle, "FF217346");

    // ── Hoja 2: Resumen por encuestador ───────────────────────────────────
    const resumenEncuestador = calcularResumenEncuestadorParaExport(filasDetalle);
    if (resumenEncuestador.length) {
      const wsEncuestador = workbook.addWorksheet("Resumen por Encuestador");
      await aplicarEncabezadoSheet(workbook, wsEncuestador, "RESUMEN RIT - ENCUESTADOS POR ENCUESTADOR", "G");
      agregarTablaSheet(wsEncuestador, resumenEncuestador, "FF1565C0");

      const totalGeneral = resumenEncuestador.reduce((s, r) => s + r["Total Encuestas"], 0);
      const totalRow = wsEncuestador.addRow(["", "", "TOTAL GENERAL", totalGeneral, "", ""]);
      totalRow.eachCell((cell) => {
        cell.font   = { bold: true };
        cell.border = {
          top: { style: "thin" }, bottom: { style: "double" },
          left: { style: "thin" }, right: { style: "thin" },
        };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8F5E9" } };
      });
    }

    // ── Hoja 3: Resumen por municipio ─────────────────────────────────────
    const resumenMunicipio = calcularResumenMunicipioParaExport(filasDetalle);
    if (resumenMunicipio.length) {
      const wsMunicipio = workbook.addWorksheet("Resumen por Municipio");
      await aplicarEncabezadoSheet(workbook, wsMunicipio, "RESUMEN RIT - ENCUESTADOS POR MUNICIPIO", "F");
      agregarTablaSheet(wsMunicipio, resumenMunicipio, "FF6A1F78");

      const totalGeneral = resumenMunicipio.reduce((s, r) => s + r["Total Encuestas"], 0);
      const totalRow = wsMunicipio.addRow(["", "TOTAL GENERAL", totalGeneral, "", ""]);
      totalRow.eachCell((cell) => {
        cell.font   = { bold: true };
        cell.border = {
          top: { style: "thin" }, bottom: { style: "double" },
          left: { style: "thin" }, right: { style: "thin" },
        };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3E5F5" } };
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `ReporteRIT_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ── Excel — descarga ──────────────────────────────────────────────────────
  const descargarExcel = async () => {
    const exportarParcial = !esAdmin || modoFiltro !== "paginado";

    if (exportarParcial) {
      if (!dataFiltrada.length) { message.warning("No hay datos para exportar"); return; }
      const filas = dataFiltrada.map((row) => ({
        "ID Encuesta":       row.idEncuesta,
        "Documento Titular": row.documentoTitular,
        "Teléfono":          row.telefono,
        "Titular":           row.titular,
        "Dirección":         row.direccion,
        "Municipio":         row.municipio,
        "Departamento":      row.departamento,
        "Encuestador":       row.promotor,
        "Fecha Carga":       row.fechaCarga,
        "Desea Participar":  Number(row.desea_participar) === 1 ? "Continua" : "No Continua",
      }));
      await generarExcel(filas);
      message.success(`Excel exportado con ${filas.length.toLocaleString("es-CO")} registros (3 pestañas)`);
      return;
    }

    setDescargando(true);
    setProgresoDescarga(0);
    setFilasDescargadas(0);

    try {
      const todos = await obtenerTodosEncuestadosEnBloques(
        (pct, cargadas) => { setProgresoDescarga(pct ?? 0); setFilasDescargadas(cargadas); },
        totalRegistros,
        500,
      );
      const filasMapeadas = mapearFilasExport(todos);
      const filasLimpias  = filasMapeadas.map(({ "Promotor ID": _pid, ...rest }) => rest);

      const filasFiltradas = hayFiltrosActivos
        ? filasLimpias.filter((row) => {
            const coincideDocumento =
              !filtroDocumento ||
              String(row["Documento Titular"] ?? "").toLowerCase().includes(filtroDocumento.toLowerCase());
            const coincideDesea =
              filtroDeseaParticipar == null ||
              (filtroDeseaParticipar === 1
                ? row["Desea Participar"] === "Continua"
                : row["Desea Participar"] === "No Continua");
            return coincideDocumento && coincideDesea;
          })
        : filasLimpias;

      setProgresoDescarga(100);
      message.success(
        hayFiltrosActivos
          ? `Exportando ${filasFiltradas.length.toLocaleString("es-CO")} de ${filasLimpias.length.toLocaleString("es-CO")} registros (3 pestañas)`
          : `Excel generado con ${filasFiltradas.length.toLocaleString("es-CO")} registros (3 pestañas)`
      );
      await generarExcel(filasFiltradas);
    } catch (error) {
      console.error(error);
      message.error("Error al descargar los datos para Excel");
    } finally {
      setDescargando(false);
      setProgresoDescarga(0);
      setFilasDescargadas(0);
    }
  };

  // ── Sincronización ────────────────────────────────────────────────────────
  const iniciarProgreso = () => {
    setProgreso(0);
    setSincronizacionFinalizada(false);
    setMensajeActual(MENSAJES_PROGRESO[0]);
    mensajeIdxRef.current = 0;
    setModalVisible(true);
    intervalRef.current = setInterval(() => {
      setProgreso((prev) => {
        if (prev >= 85) { clearInterval(intervalRef.current); return prev; }
        const siguiente = prev + Math.random() * 2 + 0.5;
        const nuevoIdx  = Math.min(Math.floor(siguiente / 12), MENSAJES_PROGRESO.length - 1);
        if (nuevoIdx !== mensajeIdxRef.current) {
          mensajeIdxRef.current = nuevoIdx;
          setMensajeActual(MENSAJES_PROGRESO[nuevoIdx]);
        }
        return Math.min(siguiente, 85);
      });
    }, 600);
  };

  const finalizarProgreso = useCallback((total) => {
    clearInterval(intervalRef.current);
    setMensajeActual("¡Sincronización completada!");
    setTotalSincronizado(total);
    let p = 85;
    const finish = setInterval(() => {
      p += 3;
      setProgreso(Math.min(p, 100));
      if (p >= 100) { clearInterval(finish); setSincronizacionFinalizada(true); }
    }, 60);
  }, []);

  const handleActualizar = async () => {
    iniciarProgreso();
    try {
      const resp = await actualizarDatosTemporalRit();
      finalizarProgreso(resp.total_registros ?? resp.total ?? 0);
      if (esAdmin) {
        if (departamentoAdmin)   await cargarTablaAdminDepartamento(departamentoAdmin, true);
        else if (municipioAdmin) await cargarTablaAdminMunicipio(municipioAdmin, true);
        else                     await cargarPaginado(cursors[cursorIndex], pageSize, false, true);
      } else {
        await cargarTablaMunicipio(true);
      }
    } catch (error) {
      console.error(error);
      clearInterval(intervalRef.current);
      setModalVisible(false);
      message.error("Error al sincronizar los datos del RIT");
    }
  };

  const cerrarModal = () => {
    setModalVisible(false);
    setProgreso(0);
    setSincronizacionFinalizada(false);
  };

  // ── Columnas tabla detalle ────────────────────────────────────────────────
  const columns = [
    { title: "ID Encuesta",  dataIndex: "idEncuesta",       key: "idEncuesta",       width: 120, align: "center" },
    { title: "N° Documento", dataIndex: "documentoTitular", key: "documentoTitular", width: 140 },
    { title: "Teléfono",     dataIndex: "telefono",         key: "telefono",         width: 140 },
    { title: "Titular",      dataIndex: "titular",          key: "titular",          ellipsis: true },
    { title: "Dirección",    dataIndex: "direccion",        key: "direccion",        ellipsis: true },
    { title: "Municipio",    dataIndex: "municipio",        key: "municipio",        width: 150 },
    { title: "Departamento", dataIndex: "departamento",     key: "departamento",     width: 150 },
    {
      title: "Encuestador", dataIndex: "promotor", key: "promotor", ellipsis: true,
      render: (val) => <Space size={4}><UserOutlined style={{ color: "#1677ff" }} />{val}</Space>,
    },
    { title: "Fecha Carga", dataIndex: "fechaCarga", key: "fechaCarga", width: 180 },
    {
      title: "Desea Participar", dataIndex: "desea_participar", key: "desea_participar",
      width: 110, align: "center",
      render: (val) => Number(val) === 1
        ? <Tag color="success">Continua</Tag>
        : <Tag color="error">No continua</Tag>,
    },
  ];

  // ── Columnas resumen por encuestador ──────────────────────────────────────
  const columnsResumenEncuestador = [
    { title: "Municipio",          dataIndex: "municipio",      key: "municipio" },
    { title: "Encuestador",        dataIndex: "encuestador",    key: "encuestador" },
    {
      title: "Total encuestas", dataIndex: "totalEncuestas", key: "totalEncuestas",
      align: "center",
      render: (val) => <Tag color="blue">{val.toLocaleString("es-CO")}</Tag>,
    },
  ];

  // ── Columnas resumen por municipio ────────────────────────────────────────
  const columnsResumenMunicipio = [
    { title: "Departamento",    dataIndex: "departamento",    key: "departamento" },
    { title: "Municipio",       dataIndex: "municipio",       key: "municipio" },
    {
      title: "Total encuestas", dataIndex: "totalEncuestas", key: "totalEncuestas",
      align: "center",
      render: (val) => <Tag color="blue">{val.toLocaleString("es-CO")}</Tag>,
    },
    {
      title: "Continua", dataIndex: "totalContinua", key: "totalContinua",
      align: "center",
      render: (val) => <Tag color="success">{val.toLocaleString("es-CO")}</Tag>,
    },
    {
      title: "No continua", dataIndex: "totalNoContinua", key: "totalNoContinua",
      align: "center",
      render: (val) => <Tag color="error">{val.toLocaleString("es-CO")}</Tag>,
    },
  ];

  const labelModo = {
    paginado:        null,
    municipio:       "municipio",
    departamento:    "departamento",
    promotor:        "encuestador",
    documento:       "documento",
    deseaParticipar: "participación",
  }[modoFiltro];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Card style={{ margin: 20 }}>
      <Title level={3}>Reporte RIT</Title>

      {/* ── Acciones principales ──────────────────────────────────────────── */}
      <Space style={{ marginBottom: 16 }} wrap>
        {permissionUpdate && (
          <Button
            type="primary"
            icon={<SyncOutlined />}
            onClick={handleActualizar}
            disabled={modalVisible || descargando}
          >
            Actualizar datos
          </Button>
        )}

        <Tooltip
          title={
            descargando
              ? `Descargando... ${filasDescargadas.toLocaleString("es-CO")} de ${totalRegistros.toLocaleString("es-CO")} registros`
              : esAdmin && modoFiltro === "paginado"
              ? `Exportar todos los registros (${totalRegistros.toLocaleString("es-CO")}) — 3 pestañas`
              : dataFiltrada.length === 0
              ? "No hay datos para exportar"
              : `Exportar ${dataFiltrada.length.toLocaleString("es-CO")} registros — 3 pestañas`
          }
        >
          <Button
            icon={descargando ? <DownloadOutlined /> : <FileExcelOutlined />}
            onClick={descargarExcel}
            loading={descargando}
            disabled={descargando || (!esAdmin && dataFiltrada.length === 0)}
            style={{ borderColor: "#217346", color: descargando ? undefined : "#217346" }}
          >
            {descargando ? `Descargando ${progresoDescarga}%` : "Descargar Excel"}
          </Button>
        </Tooltip>

        <Button
          icon={<BarChartOutlined />}
          type={modoResumen === "encuestador" ? "primary" : "default"}
          disabled={descargando}
          onClick={() => toggleResumen("encuestador")}
        >
          {modoResumen === "encuestador" ? "Ver detalle" : "Resumen por encuestador"}
        </Button>

        <Button
          icon={<BarChartOutlined />}
          type={modoResumen === "municipio" ? "primary" : "default"}
          disabled={descargando}
          onClick={() => toggleResumen("municipio")}
        >
          {modoResumen === "municipio" ? "Ver detalle" : "Resumen por municipio"}
        </Button>
      </Space>

      {/* ── Barra de progreso de descarga ─────────────────────────────────── */}
      {descargando && (
        <div style={{ marginBottom: 16, maxWidth: 500 }}>
          <Progress
            percent={progresoDescarga}
            status="active"
            strokeColor={{ "0%": "#217346", "100%": "#52c41a" }}
            format={() => (
              <Text style={{ fontSize: 12 }}>
                {filasDescargadas.toLocaleString("es-CO")} / {totalRegistros.toLocaleString("es-CO")}
              </Text>
            )}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Preparando archivo Excel (3 pestañas), no cierres esta ventana...
          </Text>
        </div>
      )}

      {/* ── Selectores municipio / departamento — solo admin ─────────────── */}
      {esAdmin && (
        <>
          <Divider orientation="left" style={{ margin: "12px 0" }}>
            <Space><FilterOutlined /> Filtrar por municipio o departamento</Space>
          </Divider>

          <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align="middle">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filtrar por municipio..."
                style={{ width: "100%" }}
                value={municipioAdmin}
                onChange={handleMunicipioAdminChange}
                loading={loadingMunicipios}
                allowClear showSearch optionFilterProp="label"
                options={municipios.map((m) => ({ value: m.municipio_id, label: m.municipio }))}
                suffixIcon={<FilterOutlined />}
                disabled={descargando}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filtrar por departamento..."
                style={{ width: "100%" }}
                value={departamentoAdmin}
                onChange={handleDepartamentoAdminChange}
                loading={loadingDepartamentos}
                allowClear showSearch optionFilterProp="label"
                options={departamentos.map((d) => ({ value: d.departamento_id, label: d.departamento }))}
                suffixIcon={<FilterOutlined />}
                disabled={descargando}
              />
            </Col>
            {(municipioAdmin || departamentoAdmin) && (
              <Col>
                <Text type="secondary">
                  Mostrando todos los registros del{" "}
                  {municipioAdmin ? "municipio" : "departamento"} seleccionado
                </Text>
              </Col>
            )}
          </Row>
        </>
      )}

      {/* ── Modal de sincronización ───────────────────────────────────────── */}
      <Modal
        open={modalVisible}
        footer={sincronizacionFinalizada
          ? <Button type="primary" onClick={cerrarModal}>Cerrar</Button>
          : null}
        closable={false} maskClosable={false} centered width={460}
        title={
          <Space>
            {sincronizacionFinalizada
              ? <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />
              : <SyncOutlined spin style={{ color: "#1677ff", fontSize: 18 }} />}
            <span>
              {sincronizacionFinalizada ? "Sincronización completada" : "Sincronizando datos RIT"}
            </span>
          </Space>
        }
      >
        <div style={{ padding: "16px 0" }}>
          <Progress
            percent={Math.round(progreso)}
            status={sincronizacionFinalizada ? "success" : "active"}
            strokeColor={sincronizacionFinalizada ? "#52c41a" : { "0%": "#1677ff", "100%": "#36cfc9" }}
          />
          <div style={{ marginTop: 12, minHeight: 44 }}>
            {sincronizacionFinalizada ? (
              <Space direction="vertical" size={2}>
                <Text strong style={{ color: "#52c41a" }}>{mensajeActual}</Text>
                <Text type="secondary">
                  Se sincronizaron{" "}
                  <Text strong>{totalSincronizado.toLocaleString("es-CO")}</Text>{" "}
                  registros. La tabla ha sido actualizada.
                </Text>
              </Space>
            ) : (
              <Text type="secondary">{mensajeActual}</Text>
            )}
          </div>
        </div>
      </Modal>

      {/* ── Tabla ─────────────────────────────────────────────────────────── */}
      <Spin spinning={loadingConsultar} tip="Cargando datos..." size="large">
        <div style={{ minHeight: loadingConsultar ? 400 : "auto" }}>

          {/* ── Bloque principal: filtros + contenido ── */}
          {(rawData.length > 0 || hayFiltrosActivos || modoFiltro !== "paginado") && !loadingConsultar && (
            <>
              <Divider orientation="left" style={{ margin: "12px 0" }}>
                Filtros de tabla
              </Divider>

              <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Input
                    placeholder="Buscar por N° documento"
                    prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                    value={filtroDocumento}
                    onChange={handleDocumentoChange}
                    inputMode="numeric"
                    allowClear
                  />
                </Col>

                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    placeholder="Filtrar por encuestador"
                    style={{ width: "100%" }}
                    value={filtroPromotor}
                    onChange={handlePromotorChange}
                    loading={loadingEncuestadores}
                    allowClear showSearch optionFilterProp="label"
                    options={encuestadores}
                  />
                </Col>

                <Col xs={24} sm={12} md={4} lg={3}>
                  <Select
                    placeholder="Desea Participar"
                    style={{ width: "100%" }}
                    value={filtroDeseaParticipar}
                    onChange={handleDeseaParticiparChange}
                    allowClear
                  >
                    <Option value={1}>Continua</Option>
                    <Option value={0}>No continua</Option>
                  </Select>
                </Col>

                <Col>
                  <Space>
                    <Button
                      type={mostrarDashboard ? "primary" : "default"}
                      icon={<BarChartOutlined />}
                      onClick={() => setMostrarDashboard((prev) => !prev)}
                    >
                      {mostrarDashboard ? "Ver tabla" : "Mostrar dashboard"}
                    </Button>
                  </Space>
                </Col>

                {hayFiltrosActivos && (
                  <Col>
                    <Button icon={<ClearOutlined />} onClick={limpiarFiltros}>
                      Limpiar
                    </Button>
                  </Col>
                )}
              </Row>

              {/* ── Empty state: sin resultados con filtros activos ── */}
              {rawData.length === 0 && !mostrarDashboard ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                  <img
                    src="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
                    alt="sin datos"
                    style={{ width: 120, marginBottom: 16, opacity: 0.45 }}
                  />
                  <Text style={{ fontSize: 16, color: "#8c8c8c" }}>
                    {esAdmin
                      ? "No se encontraron registros"
                      : "No se encontraron registros en la base de datos"}
                  </Text>
                </div>
              ) : (
                <>
                  {!mostrarDashboard && (
                    <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                      <Col>
                        <Text type="secondary">
                          {modoFiltro === "paginado" && !modoResumen ? (
                            <>
                              Mostrando{" "}
                              <Text strong>
                                {cursorIndex * pageSize + 1}–{Math.min((cursorIndex + 1) * pageSize, totalRegistros)}
                              </Text>{" "}
                              de <Text strong>{totalRegistros.toLocaleString("es-CO")}</Text> registros
                              {" "}· página <Text strong>{cursorIndex + 1}</Text>
                              {hayFiltrosActivos && <Text type="warning"> (con filtros aplicados)</Text>}
                            </>
                          ) : modoResumen && loadingResumen ? (
                            <Text type="secondary" style={{ fontStyle: "italic" }}>
                              Calculando resumen, espera un momento...
                            </Text>
                          ) : (
                            <>
                              Mostrando{" "}
                              <Text strong>
                                {modoResumen === "encuestador"
                                  ? resumenEncuestadorData.length.toLocaleString("es-CO")
                                  : modoResumen === "municipio"
                                  ? resumenMunicipioData.length.toLocaleString("es-CO")
                                  : dataFiltrada.length.toLocaleString("es-CO")}
                              </Text>
                              {hayFiltrosActivos && (
                                <Text type="secondary">
                                  {" "}de <Text strong>{rawData.length.toLocaleString("es-CO")}</Text>
                                </Text>
                              )}{" "}
                              {modoResumen === "encuestador"
                                ? "encuestadores"
                                : modoResumen === "municipio"
                                ? "municipios"
                                : `registro${dataFiltrada.length !== 1 ? "s" : ""}`}
                              {labelModo && (
                                <Text type="secondary">
                                  {" "}· filtrado por <Text strong>{labelModo}</Text>
                                </Text>
                              )}
                              {hayFiltrosActivos && <Text type="warning"> (filtros aplicados)</Text>}
                            </>
                          )}
                        </Text>
                      </Col>

                      {esAdmin && modoFiltro === "paginado" && !modoResumen && (
                        <Col>
                          <Space>
                            <Space size={4}>
                              <Text type="secondary" style={{ fontSize: 13 }}>Filas:</Text>
                              <Select
                                size="small"
                                value={pageSize}
                                onChange={handlePageSizeChange}
                                style={{ width: 70 }}
                                disabled={descargando}
                                options={PAGE_SIZE_OPTIONS.map((s) => ({ value: s, label: s }))}
                              />
                            </Space>
                            <Button
                              size="small" icon={<LeftOutlined />}
                              onClick={irPaginaAnterior}
                              disabled={cursorIndex === 0 || loadingConsultar || descargando}
                            >
                              Anterior
                            </Button>
                            <Button
                              size="small" icon={<RightOutlined />} iconPosition="end"
                              onClick={irSiguientePagina}
                              disabled={!hasMore || loadingConsultar || descargando}
                            >
                              Siguiente
                            </Button>
                          </Space>
                        </Col>
                      )}
                    </Row>
                  )}

                  {/* ── Barra de progreso del resumen ── */}
                  {loadingResumen && (
                    <div style={{ marginBottom: 16, maxWidth: 500 }}>
                      <Progress
                        percent={progresoResumen}
                        status="active"
                        strokeColor={{ "0%": "#1677ff", "100%": "#36cfc9" }}
                        format={() => (
                          <Text style={{ fontSize: 12 }}>
                            {filasResumen.toLocaleString("es-CO")} / {totalRegistros.toLocaleString("es-CO")}
                          </Text>
                        )}
                      />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Cargando resumen completo, espera un momento...
                      </Text>
                    </div>
                  )}

                  {/* ── Contenido principal ── */}
                  {mostrarDashboard ? (
                    <DashboardEncuestas data={dataFiltrada} />

                  ) : modoResumen === "encuestador" ? (
                    <Table
                      columns={columnsResumenEncuestador}
                      dataSource={loadingResumen ? [] : resumenEncuestadorData}
                      rowKey="key"
                      bordered
                      size="middle"
                      scroll={{ x: 600 }}
                      pagination={{
                        pageSize: 30,
                        showSizeChanger: true,
                        pageSizeOptions: ["30", "50", "100"],
                        showTotal: (total, range) =>
                          `${range[0]}–${range[1]} de ${total} encuestadores`,
                      }}
                      summary={(pageData) => {
                        const totalPagina = pageData.reduce((sum, r) => sum + r.totalEncuestas, 0);
                        return (
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={2}>
                              <Text strong>Total página</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} align="center">
                              <Tag color="green">{totalPagina.toLocaleString("es-CO")}</Tag>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        );
                      }}
                      expandable={{
                        expandedRowRender: (record) => (
                          <Table
                            columns={columns}
                            dataSource={record.detalle}
                            rowKey="key"
                            pagination={false}
                            size="small"
                            scroll={{ x: 1300 }}
                          />
                        ),
                      }}
                    />

                  ) : modoResumen === "municipio" ? (
                    <Table
                      columns={columnsResumenMunicipio}
                      dataSource={loadingResumen ? [] : resumenMunicipioData}
                      rowKey="key"
                      bordered
                      size="middle"
                      scroll={{ x: 700 }}
                      pagination={false}
                      summary={() => {
                        const totalEnc = resumenMunicipioData.reduce((s, r) => s + r.totalEncuestas,  0);
                        const totalCon = resumenMunicipioData.reduce((s, r) => s + r.totalContinua,   0);
                        const totalNo  = resumenMunicipioData.reduce((s, r) => s + r.totalNoContinua, 0);
                        return (
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={2}>
                              <Text strong>Total general</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} align="center">
                              <Tag color="blue">{totalEnc.toLocaleString("es-CO")}</Tag>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} align="center">
                              <Tag color="success">{totalCon.toLocaleString("es-CO")}</Tag>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={4} align="center">
                              <Tag color="error">{totalNo.toLocaleString("es-CO")}</Tag>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        );
                      }}
                      expandable={{
                        expandedRowRender: (record) => (
                          <Table
                            columns={columns}
                            dataSource={record.detalle}
                            rowKey="key"
                            size="small"
                            pagination={false}
                            scroll={{ x: 1300 }}
                          />
                        ),
                      }}
                    />

                  ) : (
                    <Table
                      columns={columns}
                      dataSource={dataFiltrada}
                      pagination={
                        modoFiltro === "paginado"
                          ? false
                          : {
                              pageSize: 30,
                              showSizeChanger: true,
                              pageSizeOptions: ["30", "50", "100"],
                              showTotal: (total, range) =>
                                `${range[0]}–${range[1]} de ${total} registros`,
                            }
                      }
                      scroll={{ x: 1300 }}
                      size="middle"
                      bordered
                    />
                  )}
                </>
              )}
            </>
          )}

          {/* ── Empty state inicial: sin datos y sin filtros ── */}
          {rawData.length === 0 && !hayFiltrosActivos && modoFiltro === "paginado" && !loadingConsultar && (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <img
                src="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
                alt="sin datos"
                style={{ width: 120, marginBottom: 16, opacity: 0.45 }}
              />
              <Text style={{ fontSize: 16, color: "#8c8c8c" }}>
                {esAdmin
                  ? "No se encontraron registros"
                  : "No se encontraron registros en la base de datos"}
              </Text>
            </div>
          )}
        </div>
      </Spin>
    </Card>
  );
};

export default ReporteRit;
